import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { AzavistaApiService, IEvent, IProject, IProjectCategory, IProjectStatus, ISearchTeamsRequest, ProjectType } from '@azavista/servicelib';
import { FieldComponentType, IField } from '@azavista/components/shared';
import { AclService, RequiredScopesService, SettingsService, SharedService, TRANSLATION_MAP, TranslationMapKey } from '../shared';

const projectStatusTranslationMap: Record<IProjectStatus, TranslationMapKey> = {
    [IProjectStatus.notStarted]: TRANSLATION_MAP.NOT_STARTED,
    [IProjectStatus.inProgress]: TRANSLATION_MAP.IN_PROGRESS,
    [IProjectStatus.completed]: TRANSLATION_MAP.COMPLETED,
    [IProjectStatus.cancelled]: TRANSLATION_MAP.CANCELLED
};

const projectTypeTranslationMap: Record<ProjectType, TranslationMapKey> = {
    [ProjectType.project]: TRANSLATION_MAP.PROJECT,
    [ProjectType.template]: TRANSLATION_MAP.PROJECT_TEMPLATE
};

const projectStatusOptions = Object.entries(IProjectStatus).map(([_, status]) => status);

@Injectable()
export class ProjectColumnsService {
    constructor(
        private sharedSvc: SharedService, private rsSvc: RequiredScopesService, private settingsSvc: SettingsService,
        private aclSvc: AclService, private apiSvc: AzavistaApiService, private translateSvc: TranslateService
    ) { }

    async getListColumns(timezone: string, showProjectType: boolean = false): Promise<IField[]> {
        const fieldsCategory = this.sharedSvc.translate(TRANSLATION_MAP.PROJECT_FIELDS);
        const result: IField[] = [];
        this.addNameField(result, fieldsCategory);
        if (showProjectType) {
            this.addProjectTypeField(result, fieldsCategory);
        }
        this.addDescriptionField(result, fieldsCategory);
        await this.addCategoryIdsField(result, fieldsCategory);
        this.addStatusField(result, fieldsCategory);
        this.addDueAtField(result, fieldsCategory, timezone);
        this.addCreatedAtField(result, fieldsCategory);
        this.addUpdatedAtField(result, fieldsCategory);

        return result;
    }

    async getCreateProjectFields(timezone: string): Promise<IField[]> {
        const fieldsCategory = this.sharedSvc.translate(TRANSLATION_MAP.PROJECT_FIELDS);
        const result: IField[] = [];

        this.addNameField(result, fieldsCategory);
        this.addDescriptionField(result, fieldsCategory);
        await this.addTeamIdSingleField(result, fieldsCategory);
        this.addDueAtField(result, fieldsCategory, timezone);

        return result;
    }

    async getCopyProjectFields(timezone: string): Promise<IField[]> {
        const fieldsCategory = this.sharedSvc.translate(TRANSLATION_MAP.PROJECT_FIELDS);
        const itemCopyCategory = this.sharedSvc.translate(TRANSLATION_MAP.CHOOSE_MODULES_TO_COPY);
        const result: IField[] = [];

        this.addNameField(result, fieldsCategory);
        this.addDescriptionField(result, fieldsCategory);
        await this.addTeamIdSingleField(result, fieldsCategory);
        this.addDueAtField(result, fieldsCategory, timezone);

        this.addCopyTeamField(result, itemCopyCategory);
        this.addCopyAssigneeTeamField(result, itemCopyCategory);
        this.addCopyAssigneeUserField(result, itemCopyCategory);

        return result;
    }

    async getUpdateField(timezone: string, showProjectType = false): Promise<IField[]> {
        const fieldsCategory = this.sharedSvc.translate(TRANSLATION_MAP.PROJECT_FIELDS);
        const result: IField[] = [];

        this.addNameField(result, fieldsCategory);
        this.addDescriptionField(result, fieldsCategory);
        await this.addCategoryIdsField(result, fieldsCategory);
        this.addStatusField(result, fieldsCategory);
        this.addDueAtField(result, fieldsCategory, timezone);
        this.addCreatedAtField(result, this.sharedSvc.translate(TRANSLATION_MAP.UPDATED_AT));
        this.addUpdatedAtField(result, this.sharedSvc.translate(TRANSLATION_MAP.UPDATED_AT));

        const nameOrder: (keyof IProject)[] = showProjectType ? [
            'name', 'type', 'description',
            'category_ids', 'status', 'due_at', 'created_at', 'updated_at'
        ] : [
            'name', 'category_ids','description',
            'status', 'due_at', 'created_at', 'updated_at'
        ];

        if (showProjectType) {
            this.addProjectTypeField(result, fieldsCategory);
        }

        return this.sharedSvc.sortByArray(result, nameOrder, x => x.name).filter(field => field);
    }

    addTeamsIdsAdvancedFilterForNonAdmins(request: ISearchTeamsRequest): void {
        if (!this.aclSvc.isAdmin()) {
            // Non-admins can see only teams which have event:create scope
            const teamIdsWithCrmCreateScope = this.aclSvc.getCurrentUserTeamIdsContainingScope(this.rsSvc.getEventCreateScope());
            request.advancedQuery = this.sharedSvc.createIdInSearchParams(teamIdsWithCrmCreateScope);
        }
    }

    private addDescriptionField(result: IField[], fieldsCategory: string) {
        result.push({
            id: 'description', name: 'description', label: this.sharedSvc.translate(TRANSLATION_MAP.DESCRIPTION), category: fieldsCategory,
            schema: { type: 'string' }, editable: true, builtin: true, visible_for_planners: true,
            mandatory_for_planners: true, component: FieldComponentType.textarea
        });
    }

    private addProjectTypeField(result: IField[], fieldsCategory: string) {
        result.push(this.sharedSvc.createSelectField({
            name: 'type',
            id: 'type',
            label: this.sharedSvc.translate(TRANSLATION_MAP.TYPE),
            category: fieldsCategory,
        }, projectTypeTranslationMap));
    }

    private addDueAtField(result: IField[], fieldsCategory: string, timezone: string) {
        result.push({
            id: 'due_at', name: 'due_at', label: this.sharedSvc.translate(TRANSLATION_MAP.DUE_AT), category: fieldsCategory,
            type: 'datetime', schema: { type: 'string', format: 'date-time'},
            component: FieldComponentType.default, editable: true, builtin: true, visible_for_planners: true,
            mandatory_for_planners: true, timezone: timezone || 'UTC'
        });
    }

    private addCreatedAtField(result: IField[], fieldsCategory: string) {
        result.push({
            id: 'created_at', name: 'created_at', label: this.sharedSvc.translate(TRANSLATION_MAP.CREATED_AT), category: fieldsCategory,
            type: 'datetime', schema: { type: 'string', format: 'date-time'},
            component: FieldComponentType.default, editable: false, builtin: false, visible_for_planners: true,
            mandatory_for_planners: false
        });
    }

    private addUpdatedAtField(result: IField[], fieldsCategory: string) {
        result.push({
            id: 'updated_at', name: 'updated_at', label: this.sharedSvc.translate(TRANSLATION_MAP.UPDATED_AT), category: fieldsCategory,
            type: 'datetime', schema: { type: 'string', format: 'date-time'},
            component: FieldComponentType.default, editable: false, builtin: false, visible_for_planners: true,
            mandatory_for_planners: false
        });
    }

    private addStatusField(result: IField[], fieldsCategory: string) {
        result.push({
            id: 'status', name: 'status', label: this.sharedSvc.translate(TRANSLATION_MAP.STATUS), category: fieldsCategory,
            schema: { type: 'string', enum: projectStatusOptions }, editable: true, builtin: true, visible_for_planners: true,
            mandatory_for_planners: false,
            attributeTranslations: projectStatusOptions.map(status => ({
                value: status,
                trans: {
                    [this.translateSvc.currentLang]: this.sharedSvc.translate(projectStatusTranslationMap[status])
                }
            }))
        });
    }

    private addNameField(result: IField[], fieldsCategory: string) {
        result.push({
            id: 'name', name: 'name', label: this.sharedSvc.translate(TRANSLATION_MAP.PROJECT), category: fieldsCategory,
            schema: { type: 'string' }, editable: true, builtin: true, visible_for_planners: true,
            mandatory_for_planners: true
        });
    }

    private async addCategoryIdsField(result: IField[], fieldsCategory: string) {
        const categories: IProjectCategory[] = await this.apiSvc.getAllPagedItems(
            req => this.apiSvc.searchProjectCategory(req),
            res => res.categories
        );
        result.push({
            id: 'category_ids', name: 'category_ids', label: this.sharedSvc.translate(TRANSLATION_MAP.CATEGORIES), category: fieldsCategory,
            editable: true, builtin: true,
            schema: { type: 'array', items: { type: 'string', enum: categories.map(({ id }) => id) } },
            attributeTranslations: categories.map(({id, name}) => ({
                value: id,
                trans: {
                    [this.translateSvc.currentLang]: name
                }
            }))
        } as IField);
    }

    private async addEventIdsField(result: IField[], fieldsCategory: string) {
        const events: IEvent[] = await this.apiSvc.getAllEvents();
        result.push({
            id: 'event_ids', name: 'event_ids', label: this.sharedSvc.translate(TRANSLATION_MAP.EVENTS), category: fieldsCategory,
            editable: true, builtin: true,
            schema: { type: 'array', items: { type: 'string', enum: events.map(({ id }) => id) } },
            attributeTranslations: events.map(({id, name}) => ({
                value: id,
                trans: {
                    [this.translateSvc.currentLang]: name
                }
            }))
        } as IField);
    }

    private async addTeamIdSingleField(result: IField[], fieldsCategory: string) {
        const teams = await this.getAllTeams();
        const teamFormField = {
            id: 'team_id', name: 'team_id', label: this.sharedSvc.translate(TRANSLATION_MAP.TEAMS), category: fieldsCategory,
            editable: true, builtin: true, mandatory_for_planners: true,
            schema: { type: 'string', enum: teams.map(({ id }) => `${id}`) },
            attributeTranslations: teams.map(({ id, name }) => ({
                value: `${id}`,
                trans: {
                    [this.translateSvc.currentLang]: name
                }
            }))
        } as IField;
        result.push(teamFormField);
    }

    private async addTeamIdArrayField(result: IField[], fieldsCategory: string) {
        const teams = await this.getAllTeams();
        const teamFormField = {
            id: 'team_ids', name: 'team_ids', label: this.sharedSvc.translate(TRANSLATION_MAP.TEAMS), category: fieldsCategory,
            editable: false, builtin: false, mandatory_for_planners: false,
            schema: { type: 'array', items: { type: 'string', enum: teams.map(({ id }) => `${id}`) }},
            attributeTranslations: teams.map(({ id, name }) => ({
                value: `${id}`,
                trans: {
                    [this.translateSvc.currentLang]: name
                }
            }))
        } as IField;
        result.push(teamFormField);
    }

    private addCopyTeamField(result: IField[], fieldsCategory: string) {
        result.push({
            id: 'copy_teams',
            name: 'copy_teams',
            type: 'boolean',
            label: this.sharedSvc.translate(TRANSLATION_MAP.COPY_TEAMS),
            editable: true,
            category: fieldsCategory,
            builtin: false,
            schema: {
              type: 'boolean'
            },
            component: FieldComponentType.default
          });
    }

    private addCopyAssigneeTeamField(result: IField[], fieldsCategory: string) {
        result.push({
            id: 'assignee_team_ids',
            name: 'assignee_team_ids',
            type: 'boolean',
            label: this.sharedSvc.translate(TRANSLATION_MAP.COPY_TEAMS_ASSIGNEE),
            editable: true,
            category: fieldsCategory,
            builtin: false,
            schema: {
              type: 'boolean'
            },
            component: FieldComponentType.default
          });
    }

    private addCopyAssigneeUserField(result: IField[], fieldsCategory: string) {
        result.push({
            id: 'assignee_ids',
            name: 'assignee_ids',
            type: 'boolean',
            label: this.sharedSvc.translate(TRANSLATION_MAP.COPY_USERS_ASSIGNEE),
            editable: true,
            category: fieldsCategory,
            builtin: false,
            schema: {
              type: 'boolean'
            },
            component: FieldComponentType.default
          });
    }

    private async getAllTeams() {
        return await this.apiSvc.getAllPagedItems(
            req => {
                this.addTeamsIdsAdvancedFilterForNonAdmins(req);
                return this.apiSvc.searchTeams(req);
            },
            res => res.teams
        );
    }
}
